import React, { useState } from 'react';
import { Phone, MapPin, Clock, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import { contactInfo, locations } from '../data/mock';
import useJsonLd from '../hooks/useJsonLd';
import usePageSeo from '../hooks/usePageSeo';

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "url": "https://www.atlregen.com/contact",
  "name": "Contact ATL Regen",
  "description": "Contact ATL Regen for regenerative medicine consultations in Atlanta, GA.",
  "mainEntity": {
    "@type": "MedicalClinic",
    "name": "ATL Regen",
    "telephone": "+14043552728",
    "url": "https://www.atlregen.com/"
  }
};

const Contact = () => {
  usePageSeo({
    title: "Contact ATL Regen | Atlanta Pain Management Consult",
    description: "Contact ATL Regen in Atlanta, GA to schedule a consultation for PRP therapy, stem cell treatment, and non-surgical pain relief today.",
    canonical: "https://www.atlregen.com/contact",
    ogTitle: "Contact ATL Regen | Atlanta Pain Management Consult",
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useJsonLd(contactSchema);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('https://bcd4d4jsiy2x7aqs4wkl64tira0tcini.lambda-url.us-east-2.on.aws/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to send');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please call us directly at (404) 355-2728.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#1B1B3A] via-[#2D2D4A] to-[#1B1B3A] text-white py-20">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
              <Mail className="h-5 w-5 text-[#20B2AA]" />
              <span className="text-sm font-medium">Get in Touch</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact ATL Regen</h1>
            <p className="text-xl text-gray-300">
              Have questions about regenerative medicine or ready to schedule your consultation? Our experienced team is here to help. Whether you're seeking treatment for knee pain, back pain, arthritis, joint pain, or sports injuries, ATL Regen provides personalized, non-surgical treatment options to restore your mobility.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12">

              {/* Contact Form */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-[#1B1B3A] mb-6">Send Us a Message</h2>

                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <CheckCircle className="h-16 w-16 text-[#20B2AA] mb-4" />
                    <h3 className="text-2xl font-bold text-[#1B1B3A] mb-2">Message Received!</h3>
                    <p className="text-gray-600">
                      Thank you for reaching out. A member of our team will contact you shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="name">
                        Full Name <span className="text-[#E63946]">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="phone">
                          Phone <span className="text-[#E63946]">*</span>
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(404) 000-0000"
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="email">
                          Email
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="location">
                        Preferred Location
                      </label>
                      <select
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#20B2AA] bg-white"
                      >
                        <option value="">Select a location</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.name}>
                            {loc.name} — {loc.city}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1" htmlFor="message">
                        Message <span className="text-[#E63946]">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your condition or question..."
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#20B2AA] resize-none"
                      />
                    </div>

                    {error && (
                      <p className="text-[#E63946] text-sm">{error}</p>
                    )}

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#E63946] hover:bg-[#D32F3D] text-white py-4 text-lg font-bold"
                    >
                      <Send className="mr-2 h-5 w-5" />
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-[#1B1B3A] mb-6">Contact Information</h2>
                  <ul className="space-y-5">
                    <li className="flex items-start gap-4">
                      <div className="bg-[#20B2AA]/10 p-3 rounded-xl">
                        <Phone className="h-5 w-5 text-[#20B2AA]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-medium">Phone</p>
                        <a href={`tel:+14043552728`} className="text-[#1B1B3A] font-semibold hover:text-[#20B2AA] transition-colors text-lg">
                          {contactInfo.phone}
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="bg-[#20B2AA]/10 p-3 rounded-xl">
                        <Clock className="h-5 w-5 text-[#20B2AA]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-medium">Hours</p>
                        <p className="text-[#1B1B3A] font-semibold">{contactInfo.hours}</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-xl font-bold text-[#1B1B3A] mb-5 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#E63946]" />
                    Our Locations
                  </h2>
                  <ul className="space-y-4">
                    {locations.map((loc) => (
                      <li key={loc.id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                        <p className="font-bold text-[#1B1B3A]">{loc.name}</p>
                        <p className="text-gray-600 text-sm">{loc.address}</p>
                        <p className="text-gray-600 text-sm">{loc.city}</p>
                        <a
                          href={loc.mapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#20B2AA] text-sm font-medium hover:underline mt-1 inline-block"
                        >
                          Get Directions →
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
